// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import { MotionMasterMessage, RequestStatus, StatusKey } from './types';

export const requestStatusResolver: { [key in StatusKey]: <T>(status: T) => RequestStatus } = {
  systemPong: () => 'succeeded',
  systemVersion: (status: MotionMasterMessage.Status.ISystemVersion) => {
    return status.version ? 'succeeded' : 'failed';
  },
  systemEvent: (status: MotionMasterMessage.Status.ISystemEvent) => {
    if (status.error) {
      return 'failed';
    }

    return 'succeeded';
  },
  deviceInfo: (status: MotionMasterMessage.Status.IDeviceInfo) => {
    return status.devices ? 'succeeded' : 'failed';
  },
  deviceParameterInfo: (status: MotionMasterMessage.Status.IDeviceParameterInfo) => {
    return status.parameters ? 'succeeded' : 'failed';
  },
  deviceParameterValues: (status: MotionMasterMessage.Status.IDeviceParameterValues) => {
    if (status.progress) {
      if (status.progress.percentage === 100) {
        return 'succeeded';
      } else {
        return 'running';
      }
    }

    if (status.parameterValues) {
      const allSucceeded = status.parameterValues.every((value) => value.success);
      if (allSucceeded) {
        return 'succeeded';
      }
    }

    return 'failed';
  },
  multiDeviceParameterValues: (status: MotionMasterMessage.Status.IMultiDeviceParameterValues) => {
    if (status.collection) {
      const items = status.collection.flat();
      const allSucceeded = items.every((item) => item.parameterValues?.every((value) => value.success));
      if (allSucceeded) {
        return 'succeeded';
      }

      if (items.every((item) => item.progress === 100)) {
        return 'succeeded';
      } else if (items.some((item) => item.progress !== null && item.progress !== undefined && item.progress < 100)) {
        return 'running';
      }
    }

    return 'failed';
  },
  deviceFileList: (status: MotionMasterMessage.Status.IDeviceFileList) => {
    if (status.error) {
      return 'failed';
    }

    return 'succeeded';
  },
  deviceFile: (status: MotionMasterMessage.Status.IDeviceFile) => {
    if (status.error) {
      return 'failed';
    }

    return 'succeeded';
  },
  deviceEvent: (status: MotionMasterMessage.Status.IDeviceEvent) => {
    if (status.error) {
      return 'failed';
    }

    return 'succeeded';
  },
  deviceFirmwareInstallation: (status: MotionMasterMessage.Status.IDeviceFirmwareInstallation) => {
    if (status.error) {
      return 'failed';
    }

    if (status.success) {
      if (status.success.code === MotionMasterMessage.Status.DeviceFirmwareInstallation.Success.Code.DONE) {
        return 'succeeded';
      }

      if (status.success.code === MotionMasterMessage.Status.DeviceFirmwareInstallation.Success.Code.UNSPECIFIED) {
        return 'succeeded';
      }
    }

    return 'running';
  },
  deviceLog: (status: MotionMasterMessage.Status.IDeviceLog) => {
    if (status.error) {
      return 'failed';
    }

    return 'succeeded';
  },
  deviceFaultReset: (status: MotionMasterMessage.Status.IDeviceFaultReset) => {
    if (status.error) {
      return 'failed';
    }

    return 'succeeded';
  },
  coggingTorqueRecording: (status: MotionMasterMessage.Status.ICoggingTorqueRecording) => {
    if (status.error) {
      return 'failed';
    }

    if (status.success) {
      if (status.success.code === MotionMasterMessage.Status.CoggingTorqueRecording.Success.Code.DONE) {
        return 'succeeded';
      }

      if (status.success.code === MotionMasterMessage.Status.CoggingTorqueRecording.Success.Code.UNSPECIFIED) {
        return 'succeeded';
      }
    }

    return 'running';
  },
  coggingTorqueData: (status: MotionMasterMessage.Status.ICoggingTorqueData) => {
    if (status.error) {
      return 'failed';
    }

    return 'succeeded';
  },
  offsetDetection: (status: MotionMasterMessage.Status.IOffsetDetection) => {
    if (status.error) {
      return 'failed';
    }

    if (status.success) {
      if (status.success.code === MotionMasterMessage.Status.OffsetDetection.Success.Code.DONE) {
        return 'succeeded';
      }

      if (status.success.code === MotionMasterMessage.Status.OffsetDetection.Success.Code.UNSPECIFIED) {
        return 'succeeded';
      }
    }

    return 'running';
  },
  plantIdentification: (status: MotionMasterMessage.Status.IPlantIdentification) => {
    if (status.error) {
      return 'failed';
    }

    if (status.success) {
      if (status.success.code === MotionMasterMessage.Status.PlantIdentification.Success.Code.DONE) {
        return 'succeeded';
      }

      if (status.success.code === MotionMasterMessage.Status.PlantIdentification.Success.Code.UNSPECIFIED) {
        return 'succeeded';
      }
    }

    return 'running';
  },
  autoTuning: (status: MotionMasterMessage.Status.IAutoTuning) => {
    if (status.error) {
      return 'failed';
    }

    if (status.success) {
      if (
        status.success.code === MotionMasterMessage.Status.AutoTuning.Success.Code.POSITION_DONE ||
        status.success.code === MotionMasterMessage.Status.AutoTuning.Success.Code.VELOCITY_DONE
      ) {
        return 'succeeded';
      }

      if (status.success === MotionMasterMessage.Status.AutoTuning.Success.Code.UNSPECIFIED) {
        return 'succeeded';
      }
    }

    return 'running';
  },
  motionController: (status: MotionMasterMessage.Status.IMotionController) => {
    if (status.error) {
      return 'failed';
    }

    return 'succeeded';
  },
  signalGenerator: (status: MotionMasterMessage.Status.ISignalGenerator) => {
    if (status.error) {
      return 'failed';
    }

    if (status.success) {
      if (status.success.code === MotionMasterMessage.Status.SignalGenerator.Success.Code.DONE) {
        return 'succeeded';
      }

      if (status.success.code === MotionMasterMessage.Status.SignalGenerator.Success.Code.PARAMETERS_SET) {
        return 'succeeded';
      }

      if (status.success.code === MotionMasterMessage.Status.SignalGenerator.Success.Code.UNSPECIFIED) {
        return 'succeeded';
      }
    }

    return 'running';
  },
  monitoringParameterValues: (status: MotionMasterMessage.Status.IMonitoringParameterValues) => {
    if (status.error) {
      return 'failed';
    }

    return 'succeeded';
  },
  deviceStop: (status: MotionMasterMessage.Status.IDeviceStop) => {
    if (status.error) {
      return 'failed';
    }

    return 'succeeded';
  },
  ethercatNetworkState: (status: MotionMasterMessage.Status.IEthercatNetworkState) => {
    if (status.error) {
      return 'failed';
    }

    return 'succeeded';
  },
  narrowAngleCalibration: (status: MotionMasterMessage.Status.INarrowAngleCalibration) => {
    if (status.error) {
      return 'failed';
    }

    if (status.success) {
      if (status.success.code === MotionMasterMessage.Status.NarrowAngleCalibration.Success.Code.DONE) {
        return 'succeeded';
      }

      if (status.success.code === MotionMasterMessage.Status.NarrowAngleCalibration.Success.Code.UNSPECIFIED) {
        return 'succeeded';
      }
    }

    return 'running';
  },
  systemIdentification: (status: MotionMasterMessage.Status.ISystemIdentification) => {
    if (status.error) {
      return 'failed';
    }

    if (status.success) {
      if (status.success.code === MotionMasterMessage.Status.SystemIdentification.Success.Code.DONE) {
        return 'succeeded';
      }

      if (status.success.code === MotionMasterMessage.Status.SystemIdentification.Success.Code.UNSPECIFIED) {
        return 'succeeded';
      }
    }

    return 'running';
  },
  circuloEncoderMagnetDistance: (status: MotionMasterMessage.Status.ICirculoEncoderMagnetDistance) => {
    if (typeof status.distance === 'number' || typeof status.position === 'number') {
      return 'succeeded';
    }

    return 'failed';
  },
  circuloEncoderNarrowAngleCalibrationProcedure: (
    status: MotionMasterMessage.Status.ICirculoEncoderNarrowAngleCalibrationProcedure
  ) => {
    if (status.error) {
      return 'failed';
    }

    if (status.success) {
      if (
        status.success.code ===
        MotionMasterMessage.Status.CirculoEncoderNarrowAngleCalibrationProcedure.Success.Code.STOPPED
      ) {
        return 'failed';
      }

      if (
        status.success.code ===
        MotionMasterMessage.Status.CirculoEncoderNarrowAngleCalibrationProcedure.Success.Code.DONE
      ) {
        return 'succeeded';
      }

      if (
        status.success ===
        MotionMasterMessage.Status.CirculoEncoderNarrowAngleCalibrationProcedure.Success.Code.UNSPECIFIED
      ) {
        return 'succeeded';
      }
    }

    return 'running';
  },
  deviceCia402State: (status: MotionMasterMessage.Status.IDeviceCiA402State) => {
    if (status.error) {
      return 'failed';
    }

    return 'succeeded';
  },
  systemLog: (status: MotionMasterMessage.Status.ISystemLog) => {
    if (status.content) {
      return 'succeeded';
    }

    return 'failed';
  },
  deviceSiiRestore: (status: MotionMasterMessage.Status.IDeviceSiiRestore) => {
    if (status.error) {
      return 'failed';
    }

    if (status.success) {
      if (status.success.code === MotionMasterMessage.Status.DeviceSiiRestore.Success.Code.DONE) {
        return 'succeeded';
      }

      if (status.success.code === MotionMasterMessage.Status.DeviceSiiRestore.Success.Code.UNSPECIFIED) {
        return 'succeeded';
      }
    }

    return 'running';
  },
  openLoopFieldControl: (status: MotionMasterMessage.Status.IOpenLoopFieldControl) => {
    if (status.error) {
      return 'failed';
    }

    if (status.success) {
      if (status.success.code === MotionMasterMessage.Status.OpenLoopFieldControl.Success.Code.DONE) {
        return 'succeeded';
      }

      if (status.success.code === MotionMasterMessage.Status.OpenLoopFieldControl.Success.Code.UNSPECIFIED) {
        return 'succeeded';
      }
    }

    return 'running';
  },
  fullAutoTuning: (status: MotionMasterMessage.Status.IFullAutoTuning) => {
    if (status.error) {
      return 'failed';
    }

    if (status.success) {
      if (
        status.success.code === MotionMasterMessage.Status.FullAutoTuning.Success.Code.POSITION_STOPPED ||
        status.success.code === MotionMasterMessage.Status.FullAutoTuning.Success.Code.VELOCITY_STOPPED
      ) {
        return 'failed';
      }

      if (
        status.success.code === MotionMasterMessage.Status.FullAutoTuning.Success.Code.POSITION_DONE ||
        status.success.code === MotionMasterMessage.Status.FullAutoTuning.Success.Code.VELOCITY_DONE
      ) {
        return 'succeeded';
      }

      if (status.success === MotionMasterMessage.Status.FullAutoTuning.Success.Code.UNSPECIFIED) {
        return 'succeeded';
      }
    }

    return 'running';
  },
  circuloEncoderConfiguration: (status: MotionMasterMessage.Status.ICirculoEncoderConfiguration) => {
    if (status.error) {
      return 'failed';
    }

    if (status.success) {
      if (status.success.code === MotionMasterMessage.Status.CirculoEncoderConfiguration.Success.Code.DONE) {
        return 'succeeded';
      }

      if (status.success.code === MotionMasterMessage.Status.CirculoEncoderConfiguration.Success.Code.UNSPECIFIED) {
        return 'succeeded';
      }
    }

    return 'running';
  },
  osCommand: (status: MotionMasterMessage.Status.IOsCommand) => {
    if (status.error) {
      return 'failed';
    }

    if (status.success) {
      if (status.success.code === MotionMasterMessage.Status.OsCommand.Success.Code.DONE) {
        return 'succeeded';
      }

      if (status.success.code === MotionMasterMessage.Status.OsCommand.Success.Code.UNSPECIFIED) {
        return 'succeeded';
      }
    }

    return 'running';
  },
  forwardCall: (status: MotionMasterMessage.Status.IForwardCall) => {
    if (status.error) {
      return 'failed';
    }
    return 'succeeded';
  },
  systemInformation: (status: MotionMasterMessage.Status.ISystemInformation) => {
    return status.version ? 'succeeded' : 'failed';
  },
  pdoMode: (status: MotionMasterMessage.Status.IPdoMode) => {
    if (status.error) {
      return 'failed';
    }
    return 'succeeded';
  },
  deviceSii: (status: MotionMasterMessage.Status.IDeviceSii) => {
    if (status.error) {
      return 'failed';
    }

    return 'succeeded';
  },
};
